import { useRef, useState } from 'react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Upload, Loader2 } from 'lucide-react'
import { userApi } from '../api/userApi'
import type { User } from '@/types/entities'

interface ProfileImageUploadProps {
  user: User
  onUpload: (file: File) => void
  isUploading?: boolean
}

export function ProfileImageUpload({
  user,
  onUpload,
  isUploading = false,
}: ProfileImageUploadProps) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file')
      return
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('File size must be less than 5MB')
      return
    }

    // Create preview
    const reader = new FileReader()
    reader.onloadend = () => {
      setPreviewUrl(reader.result as string)
    }
    reader.readAsDataURL(file)

    onUpload(file)
  }

  const triggerFileInput = () => {
    fileInputRef.current?.click()
  }

  const profileImageUrl = previewUrl || userApi.getUserImageUrl(user.id, user.signature)
  const initials = `${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}`.toUpperCase()

  return (
    <Card className="shadow-subtle">
      <CardContent className="flex flex-col items-center gap-4 p-6">
        <Avatar className="h-32 w-32">
          <AvatarImage src={profileImageUrl} alt={`${user.firstName} ${user.lastName}`} />
          <AvatarFallback className="text-3xl">{initials}</AvatarFallback>
        </Avatar>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleFileSelect}
          disabled={isUploading}
        />

        <Button
          onClick={triggerFileInput}
          disabled={isUploading}
          variant="outline"
          className="w-full"
        >
          {isUploading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Uploading...
            </>
          ) : (
            <>
              <Upload className="mr-2 h-4 w-4" />
              Upload Picture
            </>
          )}
        </Button>

        <p className="text-xs text-muted-foreground text-center">
          Recommended: Square image, at least 400x400px
          <br />
          Max file size: 5MB
        </p>
      </CardContent>
    </Card>
  )
}

